import type {
  ExportAppearanceOptions,
  ExportSelection,
  ExportTableOptions,
} from '../schemas/export.js';
import type { Noyau } from '../schemas/noyau.js';
import { ACTIVATION_GROUP_LABEL, type ExportModel } from './build-export-model.js';
import { estimateWrappedLineCount, resolveExportRowHeights } from './estimate-row-heights.js';
import { headerThemeStyle } from './header-theme-style.js';
import {
  CSS_PX_PER_PT,
  DEFAULT_COLUMN_WIDTH_PX,
  DEFAULT_ROW_HEIGHT_PX,
  DENSITY_PADDING_PX,
  HEADER_ROW_RESERVED_PX,
  LINE_HEIGHT_FACTOR,
  MARGIN_PX,
  ROW_LABEL_WIDTH_PX,
  pageGeometry,
} from './page-geometry.js';
import type { BulletLine } from './parse-bullet-lines.js';
import { TITILLIUM_FAMILY } from './titillium-font-face.js';
import { buildTransposedSections } from './transposed.js';

/**
 * The styled Excel sheet (issue #12, spec F1 « stylé matrice »), described once
 * as plain data. The backend's exceljs writer and the export page's indicative
 * preview both consume this layout, so the preview cannot describe a different
 * sheet from the file — the role `buildPageLayout` plays for the PDF.
 *
 * Everything here is in Excel's own units where Excel has one: row heights in
 * points, column widths in characters (with the source pixels kept beside them
 * for the preview), merges in 1-based sheet coordinates, the header rows first.
 *
 * Both table structures (issues #29, #30) are described by the same shape:
 * header rows of styled cells (none in the transposed blocks), body rows of
 * styled cells, merges and a frozen split (0/0 in the transposed blocks). The
 * writer and the preview translate it without knowing which structure it is.
 */

/** A name, not an embedded font: xlsx cannot carry font bytes, so a machine without Titillium substitutes. */
export const EXCEL_FONT_FAMILY = TITILLIUM_FAMILY;
export const EXCEL_FONT_SIZE_PT = 10;
/** The palier label: the grid's bold 1.1em of the 10 pt body (issue #16, no number). */
export const EXCEL_PALIER_LABEL_SIZE_PT = 11;
/** Excel's hard ceiling on a row height. */
export const EXCEL_MAX_ROW_HEIGHT_PT = 409;
/** Excel's default row height: the blank row between two transposed blocks. */
export const EXCEL_DEFAULT_ROW_HEIGHT_PT = 15;
/**
 * The width of the transposed sheet's two columns together: the content width of
 * an A4 landscape page at normal margins, the PDF's default (1123 - 2 x 57 = 1009).
 */
export const EXCEL_TRANSPOSED_CONTENT_WIDTH_PX = (() => {
  const geometry = pageGeometry({ format: 'a4', orientation: 'landscape', margins: 'normales' });
  return geometry.pageWidthPx - 2 * MARGIN_PX.normales;
})();
/** CSS px are 1/96 in, points 1/72 in. */
export const EXCEL_PT_PER_PX = 0.75;

/**
 * The document colours the HTML/PDF stylesheet uses for borders, label cells
 * and zebra stripes (spec R5). Duplicated from `render-export-html.ts` rather
 * than moved, so the paged output stays untouched byte-for-byte;
 * `excel-sheet-layout.test.ts` renders the HTML and fails if the two drift.
 */
export const EXCEL_BORDER = {
  fines: { style: 'thin', hex: '#d9d6d2' },
  marquees: { style: 'medium', hex: '#b3aea8' },
} as const;
export const EXCEL_LABEL_FILL_HEX = '#f7f6f4';
export const EXCEL_ZEBRA_FILL_HEX = '#faf9f7';

export interface ExcelCellStyle {
  /** `null`: the cell is left unpainted. */
  fillHex: string | null;
  /** `null`: the sheet's default text colour. */
  fontHex: string | null;
  bold: boolean;
  sizePt: number;
}

export interface ExcelSheetColumn {
  widthPx: number;
  widthChars: number;
}

export interface ExcelSheetCell {
  /**
   * The cell's lines joined with newlines; each bullet line is written as
   * "• " (U+2022 and a space) then its text. Excel has no list inside a cell,
   * so the marker is plain text. A cell covered by a merge carries `''`.
   */
  text: string;
  style: ExcelCellStyle;
}

export interface ExcelSheetHeaderRow {
  /** One cell per sheet column. */
  cells: ExcelSheetCell[];
  heightPt: number;
}

export interface ExcelSheetRow {
  /**
   * The model row's key (`columns` structure); a position-derived key in the
   * `transpose` structure, where a row is a block title, a palier or a blank.
   */
  key: string;
  /**
   * `activation` and `dimension`: a row of the `columns` structure. `title`,
   * `palier` and `blank`: the rows of a transposed block (its merged title, one
   * palier line, and the empty row between two blocks).
   */
  kind: 'activation' | 'dimension' | 'title' | 'palier' | 'blank';
  /** One cell per sheet column. A `blank` row's cells are empty and carry no border. */
  cells: ExcelSheetCell[];
  heightPt: number;
}

export interface ExcelSheetMerge {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

export interface ExcelSheetLayout {
  columns: ExcelSheetColumn[];
  /** None (transposed blocks), one or two rows, the first on sheet row 1. */
  headerRows: ExcelSheetHeaderRow[];
  /** Below the header rows. */
  rows: ExcelSheetRow[];
  /** 1-based sheet coordinates. */
  merges: ExcelSheetMerge[];
  /** Columns held on the left, header rows held on top. 0/0: nothing is held. */
  frozen: { xSplit: number; ySplit: number };
  border: { style: 'thin' | 'medium'; hex: string };
}

export interface BuildExcelSheetLayoutInput {
  model: ExportModel;
  activationLevierNames: ExportSelection['activationLevierNames'];
  table: ExportTableOptions;
  appearance: ExportAppearanceOptions;
  noyau: Noyau | null;
  /** The *merged* widths (`viewState.columnWidths` ⊕ `table.columnWidths`), keyed by palier number. */
  columnWidths: Record<string, number>;
  /** `matrix.viewState.rowHeights`, keyed by `${row.kind}:${row.key}` — read in `ecran` mode only. */
  viewStateRowHeights: Record<string, number>;
}

/**
 * Pixels to Excel's character-width unit: `(px − 5) / 7`, the conversion for a
 * 7 px maximum-digit width plus 5 px of cell padding. Rounded to two decimals,
 * never below one character.
 */
export function pxToExcelWidthChars(px: number): number {
  return Math.max(1, Math.round(((px - 5) / 7) * 100) / 100);
}

function pxToPt(px: number): number {
  return Math.round(px * EXCEL_PT_PER_PX * 100) / 100;
}

function clampRowHeightPt(pt: number): number {
  return Math.min(EXCEL_MAX_ROW_HEIGHT_PT, pt);
}

function column(widthPx: number): ExcelSheetColumn {
  return { widthPx, widthChars: pxToExcelWidthChars(widthPx) };
}

function headerCellStyle(
  appearance: ExportAppearanceOptions,
  palierNumber: number,
  noyau: Noyau | null,
): ExcelCellStyle {
  const style = headerThemeStyle(appearance.headerTheme, palierNumber, noyau);
  return {
    fillHex: style?.background ?? null,
    fontHex: style?.color ?? null,
    bold: true,
    sizePt: EXCEL_PALIER_LABEL_SIZE_PT,
  };
}

/** A label cell: on the label fill, bold unless it is a levier name sitting beside its group label. */
function labelCell(text: string, bold: boolean): ExcelSheetCell {
  return {
    text,
    style: { fillHex: EXCEL_LABEL_FILL_HEX, fontHex: null, bold, sizePt: EXCEL_FONT_SIZE_PT },
  };
}

/** A content cell: the sheet's body text, on the zebra stripe when its row is striped. */
function valueCell(
  text: string,
  appearance: ExportAppearanceOptions,
  zebra: boolean,
): ExcelSheetCell {
  return {
    text,
    style: {
      fillHex: zebra && appearance.zebra ? EXCEL_ZEBRA_FILL_HEX : null,
      fontHex: null,
      bold: false,
      sizePt: EXCEL_FONT_SIZE_PT,
    },
  };
}

function linesText(lines: readonly BulletLine[]): string {
  return lines.map((line) => (line.hasBullet ? `• ${line.text}` : line.text)).join('\n');
}

function cellText(model: ExportModel, rowKey: string, palierNumber: number): string {
  return linesText(model.cellContent[rowKey]?.[palierNumber] ?? []);
}

/** An empty, unpainted cell: the blank row between two transposed blocks. */
function blankCell(): ExcelSheetCell {
  return {
    text: '',
    style: { fillHex: null, fontHex: null, bold: false, sizePt: EXCEL_FONT_SIZE_PT },
  };
}

function linePx(fontPx: number): number {
  return Math.ceil(fontPx * LINE_HEIGHT_FACTOR);
}

/**
 * The height a plain (non-bullet) label needs at `widthPx`. Excel never grows a
 * row that carries an explicit height, and never autofits a merged cell at all,
 * so a long title or name would be clipped unless the estimate accounts for it.
 */
function labelHeightPx(labels: string[], widthPx: number, fontPt: number): number {
  const fontPx = fontPt * CSS_PX_PER_PT;
  const padding = DENSITY_PADDING_PX.compact;
  const lines = Math.max(
    1,
    ...labels.map((label) =>
      label === '' ? 1 : estimateWrappedLineCount(label, false, widthPx, padding, fontPx),
    ),
  );
  return lines * linePx(fontPx) + 2 * padding;
}

/**
 * The `columns` structure: palier columns, one row per model row, the header on
 * sheet row 1. Reproduces the pre-#12 merge rules exactly: the activation
 * block's label column is merged vertically unless `inline`, and in `column`
 * mode a dimension title spans both label columns (the HTML's `colspan="2"`).
 */
function buildColumnsLayout(input: BuildExcelSheetLayoutInput): ExcelSheetLayout {
  const { model, appearance } = input;

  // Model-derived, as `renderExcel` always did: `column` mode is retained in the
  // selection when the activation row is unticked, and reserving the levier-name
  // column on the mode alone would leave a blank column B.
  const hasActivationRows = model.rows.some((row) => row.kind === 'activation');
  const labelColumnCount: 1 | 2 =
    input.activationLevierNames === 'column' && hasActivationRows ? 2 : 1;

  const heightsPx = resolveExportRowHeights({
    model,
    table: { ...input.table, density: 'compact' },
    fontSizePt: EXCEL_FONT_SIZE_PT,
    columnWidths: input.columnWidths,
    viewStateRowHeights: input.viewStateRowHeights,
  });

  const firstActivationIndex = model.rows.findIndex((row) => row.kind === 'activation');
  const rows: ExcelSheetRow[] = model.rows.map((row, index) => {
    let labels: string[];
    if (row.kind === 'dimension') {
      labels = labelColumnCount === 2 ? [row.label, ''] : [row.label];
    } else if (input.activationLevierNames === 'inline') {
      labels = [row.label];
    } else {
      // `hidden` and `column`: the six rows share the group label, carried by the
      // first row's first cell — the master of the vertical merge.
      const groupCell = index === firstActivationIndex ? ACTIVATION_GROUP_LABEL : '';
      labels = labelColumnCount === 2 ? [groupCell, row.label] : [groupCell];
    }
    const valueHeightPx = heightsPx[`${row.kind}:${row.key}`] ?? DEFAULT_ROW_HEIGHT_PX;
    // A dimension title in `column` mode is merged across both label columns,
    // hence the double width.
    const labelWidthPx =
      row.kind === 'dimension' && labelColumnCount === 2
        ? 2 * ROW_LABEL_WIDTH_PX
        : ROW_LABEL_WIDTH_PX;
    const heightPx =
      input.table.rowHeightMode === 'contenu'
        ? Math.max(valueHeightPx, labelHeightPx(labels, labelWidthPx, EXCEL_FONT_SIZE_PT))
        : valueHeightPx;
    // The row title is bold; the levier-name column of `column` mode is not
    // (the renderer's `export-row-label` 600 vs `export-levier-label` 400).
    const zebra = index % 2 === 1; // The stylesheet's `tbody tr:nth-child(even)`.
    return {
      key: row.key,
      kind: row.kind,
      cells: [
        ...labels.map((label, labelIndex) =>
          labelCell(label, !(labelIndex === 1 && row.kind === 'activation')),
        ),
        ...model.columns.map((palier) =>
          valueCell(cellText(model, row.key, palier.number), appearance, zebra),
        ),
      ],
      heightPt: clampRowHeightPt(pxToPt(heightPx)),
    };
  });

  // Header on sheet row 1, so data row `index` sits on sheet row `index + 2`.
  const merges: ExcelSheetMerge[] = [];
  const activationSheetRows = model.rows.flatMap((row, index) =>
    row.kind === 'activation' ? [index + 2] : [],
  );
  const firstSheetRow = activationSheetRows[0];
  const lastSheetRow = activationSheetRows[activationSheetRows.length - 1];
  if (
    input.activationLevierNames !== 'inline' &&
    firstSheetRow !== undefined &&
    lastSheetRow !== undefined &&
    lastSheetRow > firstSheetRow
  ) {
    merges.push({ top: firstSheetRow, left: 1, bottom: lastSheetRow, right: 1 });
  }
  if (labelColumnCount === 2) {
    model.rows.forEach((row, index) => {
      if (row.kind === 'dimension') {
        merges.push({ top: index + 2, left: 1, bottom: index + 2, right: 2 });
      }
    });
  }

  return {
    columns: [
      ...Array.from({ length: labelColumnCount }, () => column(ROW_LABEL_WIDTH_PX)),
      ...model.columns.map((palier) =>
        column(input.columnWidths[String(palier.number)] ?? DEFAULT_COLUMN_WIDTH_PX),
      ),
    ],
    headerRows: [
      {
        cells: [
          ...Array.from({ length: labelColumnCount }, () => labelCell('', false)),
          ...model.columns.map((palier) => ({
            text: palier.label,
            style: headerCellStyle(appearance, palier.number, input.noyau),
          })),
        ],
        heightPt: pxToPt(HEADER_ROW_RESERVED_PX),
      },
    ],
    rows,
    merges,
    frozen: { xSplit: labelColumnCount, ySplit: 1 },
    border: { ...EXCEL_BORDER[appearance.borders] },
  };
}

/**
 * The `transpose` structure (issues #29, #30): the HTML transposed layout as
 * stacked blocks, in two columns and with no header row. `buildTransposedSections`
 * (the function the HTML and PDF use) decides the blocks, their order, titles and
 * cell contents; each block is a title row merged over A:B (bold, on the label
 * fill) then one row per palier — A the palier label styled as the header theme
 * styles a palier header, B the cell's lines, bullets as "• ". A blank row with
 * no border separates two blocks.
 *
 * The title is sized at A + B and a palier row at its content at B's width, never
 * under its label; `rowHeightMode` is ignored (the remembered heights belong to
 * the grid's rows, which have no row here). There is no zebra, no frozen split
 * and no header row to repeat: Excel can only freeze the top of the sheet, not
 * each block's title.
 */
function buildTransposedLayout(input: BuildExcelSheetLayoutInput): ExcelSheetLayout {
  const { model, appearance } = input;
  const fontPx = EXCEL_FONT_SIZE_PT * CSS_PX_PER_PT;
  const padding = DENSITY_PADDING_PX.compact;
  const contentWidthPx = EXCEL_TRANSPOSED_CONTENT_WIDTH_PX;
  const valueWidthPx = contentWidthPx - ROW_LABEL_WIDTH_PX;

  const sections = buildTransposedSections({
    model,
    fontSizePt: EXCEL_FONT_SIZE_PT,
    density: 'compact',
    contentWidthPx,
  });

  const rows: ExcelSheetRow[] = [];
  const merges: ExcelSheetMerge[] = [];
  sections.forEach((section, sectionIndex) => {
    if (sectionIndex > 0) {
      rows.push({
        key: `blank:${sectionIndex}`,
        kind: 'blank',
        cells: [blankCell(), blankCell()],
        heightPt: EXCEL_DEFAULT_ROW_HEIGHT_PT,
      });
    }
    // No header row: the sheet row of the next row is its 1-based position.
    const titleSheetRow = rows.length + 1;
    merges.push({ top: titleSheetRow, left: 1, bottom: titleSheetRow, right: 2 });
    rows.push({
      key: `title:${sectionIndex}`,
      kind: 'title',
      cells: [labelCell(section.title, true), labelCell('', true)],
      heightPt: clampRowHeightPt(
        pxToPt(labelHeightPx([section.title], contentWidthPx, EXCEL_FONT_SIZE_PT)),
      ),
    });
    for (const row of section.rows) {
      const valueLines = Math.max(
        1,
        row.lines.reduce(
          (count, line) =>
            count +
            estimateWrappedLineCount(line.text, line.hasBullet, valueWidthPx, padding, fontPx),
          0,
        ),
      );
      const heightPx = Math.max(
        valueLines * linePx(fontPx) + 2 * padding,
        labelHeightPx([row.palier.label], ROW_LABEL_WIDTH_PX, EXCEL_PALIER_LABEL_SIZE_PT),
      );
      rows.push({
        key: `${sectionIndex}:${row.palier.number}`,
        kind: 'palier',
        cells: [
          {
            text: row.palier.label,
            style: headerCellStyle(appearance, row.palier.number, input.noyau),
          },
          valueCell(linesText(row.lines), appearance, false),
        ],
        heightPt: clampRowHeightPt(pxToPt(heightPx)),
      });
    }
  });

  return {
    columns: [column(ROW_LABEL_WIDTH_PX), column(valueWidthPx)],
    headerRows: [],
    rows,
    merges,
    frozen: { xSplit: 0, ySplit: 0 },
    border: { ...EXCEL_BORDER[appearance.borders] },
  };
}

/**
 * Builds the sheet for `table.structure`. `table.density` is ignored — Excel has
 * no cell padding — and the `contenu` estimate is taken at `compact`, the nearest
 * to Excel's own ≈2 px cell margin (decision X4).
 */
export function buildExcelSheetLayout(input: BuildExcelSheetLayoutInput): ExcelSheetLayout {
  return input.table.structure === 'transpose'
    ? buildTransposedLayout(input)
    : buildColumnsLayout(input);
}
